/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Country,
  Keyword,
  UpdatedFilterLinkCard
} from "../../../types/filter-link-card";

import {
  flag_sweden,
  flag_norway,
  flag_denmark,
  flag_finland,
  flag_italy,
  flag_germany,
  flag_austria,
  flag_switzerland,
  flag_greece,
  flag_poland,
  flag_croatia,
  flag_chechia,
  flag_hungary,
  flag_serbia,
  flag_slovakia,
  flag_russia,
  flag_slovenia
} from "designsystem/assets/img_flags";

import {
  all_inactive,
  all_active,
  ecom_inactive,
  ecom_active,
  pointofsales_inactive,
  pointofsales_active,
  digitalservices_inactive,
  digitalservices_active,
  banks_inactive,
  banks_active
} from "designsystem/assets/img_filters";

export const isCountryMatch = (
  item: UpdatedFilterLinkCard,
  country: string | null
): boolean => {
  // Check if applicable to all countries
  if (country === "All") return true;

  // Handle items with a 'countries' property (array of countries)
  if (item.countries) {
    return item.countries.some((c: Country) => c.name === country);
  }

  // Handle items with a 'country' property (can be a single country or an array)
  if (item.country) {
    if (Array.isArray(item.country)) {
      return item.country.includes(country);
    } else {
      return item.country === country;
    }
  }

  // If item doesn't specify a country or countries, assume it applies to all
  return true;
};

export const isKeywordMatch = (
  card: UpdatedFilterLinkCard,
  keyword: string
): boolean => {
  // Check if applicable to all keywords
  if (keyword === "All") return true;

  // Handle items with a 'keywords' property (array of keywords)
  if (card.keywords) {
    return card.keywords.some((k: Keyword) => k.name === keyword);
  }

  // (case for faq item) Handle items with a 'topics' property (can be a single topics or an array)
  if (card.topics) {
    if (Array.isArray(card.topics)) {
      return card.topics.includes(keyword);
    } else {
      return card.topics === keyword;
    }
  }

  // If item doesn't specify a keyword or keywords, assume it applies to all
  return true;
};

// Filter out products/cards/faq items based on selected country and/or keyword/btn
export const filterCards = (
  items: any[],
  country?: string | null,
  keyword?: string | null
): any[] => {
  return items?.filter((item) => {
    // Check for country match
    const countryMatch = country ? isCountryMatch(item, country) : true;

    // Check for keyword match if keyword is provided
    const keywordMatch = keyword ? isKeywordMatch(item, keyword) : true;

    return countryMatch && keywordMatch;
  });
};

// extracting countryFlagMap as use reusable util function
type CountryFlagKeywordIconMapProp = { [key: string]: string };

const countryFlagMap: CountryFlagKeywordIconMapProp = {
  Sweden: flag_sweden,
  Norway: flag_norway,
  Denmark: flag_denmark,
  Finland: flag_finland,
  Italy: flag_italy,
  Germany: flag_germany,
  Austria: flag_austria,
  Switzerland: flag_switzerland,
  Greece: flag_greece,
  Poland: flag_poland,
  Croatia: flag_croatia,
  "Czech Republic": flag_chechia,
  Hungary: flag_hungary,
  Serbia: flag_serbia,
  Slovakia: flag_slovakia,
  Russia: flag_russia,
  Slovenia: flag_slovenia
};

// using with didomyCountry prop
// eslint-disable-next-line react-hooks/exhaustive-deps
export const countryCodeToNameMap: CountryFlagKeywordIconMapProp = {
  SE: "Sweden",
  NO: "Norway",
  DK: "Denmark",
  FI: "Finland",
  IT: "Italy",
  DE: "Germany",
  AT: "Austria",
  CH: "Switzerland",
  GR: "Greece",
  PL: "Poland",
  HR: "Croatia",
  CZ: "Czech Republic",
  HU: "Hungary",
  RS: "Serbia",
  SK: "Slovakia",
  RU: "Russia",
  SI: "Slovenia"
  // Add other country codes and their corresponding names here
};

export const getCountryFlag = (countryName: any) =>
  countryFlagMap[countryName] || "";

// extracting filterIconMap as use reusable util function
// Map keywords to inactive icons
const keywordsIconMap: CountryFlagKeywordIconMapProp = {
  All: all_inactive,
  "E-commerce": ecom_inactive,
  "Point-of-sale": pointofsales_inactive,
  "Digital services": digitalservices_inactive,
  Banks: banks_inactive
};

// Map keywords to active icons
export const keywordsIconMapActive: CountryFlagKeywordIconMapProp = {
  All: all_active,
  "E-commerce": ecom_active,
  "Point-of-sale": pointofsales_active,
  "Digital services": digitalservices_active,
  Banks: banks_active
};

export const getKeywordIcon = (keywordName: any) =>
  keywordsIconMap[keywordName] || "";

// extracting faqItemTopicsFilterIconMap as use reusable util function
const keywordsFaqItemTopicsFilterIconMap: any = {
  //All: all_inactive,
  Billing: ecom_inactive,
  Checkout: pointofsales_inactive,
  Credentials: digitalservices_inactive
  // add here rest of faq filter topics
};

export const getFaqItemTopicsFilterIcon = (keywordName: any) =>
  keywordsFaqItemTopicsFilterIconMap[keywordName] || "";

// handle both array and object types to add country details (name and flag) and/or keywords
export const getUpdatedObjectWithCountriesKeywords = (data: any) => {
  // Handle the case where input is an array (like filterLinkCards)
  if (Array.isArray(data)) {
    return data.map((item) => {
      const countries = item.country.split(",").map((country: string) => ({
        name: country.trim(),
        flag: getCountryFlag(country.trim())
      }));

      const keywords = item.keyword.split(",").map((keyword: string) => ({
        name: keyword.trim(),
        icon: getKeywordIcon(keyword.trim())
      }));

      return {
        ...item,
        countries,
        keywords
      };
    });
  }

  // Handle the case where input is an object (like selectItemComponent - select country)
  if (data && typeof data === "object") {
    const updatedData = { ...data };

    updatedData.updatedItems = data.items.map((updatedItem: string) => ({
      name: updatedItem,
      flag: getCountryFlag(updatedItem)
    }));

    return updatedData;
  }

  // Return the input as-is if it's neither an array nor a suitable object
  return data;
};
